import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, X } from "lucide-react";
import { API, money, monthLabel } from "../lib/api";

const empty = { name: "", monthly_budget: "" };

const BudgetRow = ({ c, onEdit, onDelete }) => {
  const level = c.percent >= 100 ? "over" : c.percent >= 80 ? "warn" : "ok";
  return <div className="budget-row" data-testid={`budget-row-${c.id}`}>
    <div className="budget-row-head"><strong>{c.name}</strong><div className="row-actions">
      <button data-testid={`edit-category-${c.id}`} onClick={() => onEdit(c)} title={c.budget_set ? "Ubah kategori atau anggaran" : "Atur anggaran"}><Pencil size={15} /></button>
      <button data-testid={`delete-category-${c.id}`} onClick={() => onDelete(c)} title="Hapus"><Trash2 size={15} /></button>
    </div></div>
    <div className={`progress ${c.budget_set ? level : "unset"}`}><span style={{ width: `${Math.min(c.percent, 100)}%` }} /></div>
    <div className="budget-row-meta"><span>{c.budget_set ? `${money(c.spent)} dari ${money(c.budget)}` : `${money(c.spent)} terpakai`}</span><span className={c.budget_set ? level : "budget-unset-label"}>{c.budget_set ? `${c.percent}%${c.remaining < 0 ? ` · lewat ${money(-c.remaining)}` : ` · sisa ${money(c.remaining)}`}` : "Belum diatur"}</span></div>
  </div>;
};

export default function Budgets() {
  const [report, setReport] = useState(null);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => Promise.all([axios.get(`${API}/reports`), axios.get(`${API}/categories`)]).then(([r, c]) => {
    setReport(r.data);
    const usage = new Map(r.data.by_category.map(item => [item.id, item]));
    setCategories(c.data.map(item => {
      const stats = usage.get(item.id);
      const spent = stats?.spent || 0;
      return { ...item, spent, percent: item.budget_set && item.budget ? Math.round(spent / item.budget * 1000) / 10 : 0, remaining: item.budget_set ? item.budget - spent : null };
    }));
  }), []);
  useEffect(() => { load(); }, [load]);
  const submit = async (e) => {
    e.preventDefault();
    const body = { name: form.name, monthly_budget: form.monthly_budget === "" ? null : Number(form.monthly_budget) };
    try {
      if (editing) await axios.put(`${API}/categories/${editing}`, body);
      else await axios.post(`${API}/categories`, body);
      toast.success(editing ? "Anggaran diperbarui" : "Kategori ditambahkan"); setForm(empty); setEditing(null); load();
    } catch (err) { toast.error(err.response?.data?.detail?.toString() || "Gagal menyimpan"); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Hapus kategori ${c.name}?`)) return;
    try { await axios.delete(`${API}/categories/${c.id}`); toast.success("Kategori dihapus"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Gagal menghapus"); }
  };
  const edit = (c) => { setEditing(c.id); setForm({ name: c.name, monthly_budget: c.budget_set ? c.budget : "" }); };
  if (!report) return <div className="loading-screen">Memuat anggaran...</div>;
  const s = report.summary;
  return <>
    <div className="page-heading"><div><p className="eyebrow">PERENCANAAN · {monthLabel(report.month).toUpperCase()}</p><h1>Anggaran & kategori</h1><p className="muted">Batas anggaran berlaku per bulan dan otomatis dihitung ulang setiap awal bulan.</p></div>
      <div className="review-summary"><strong data-testid="budget-total-remaining">{s.budget_set ? money(s.remaining) : "Belum diatur"}</strong><span>{s.budget_set ? `sisa dari ${money(s.budget)}` : "Atur nilai bulanan per kategori"}</span></div></div>
    <div className="user-management">
      <section className="section-panel">
        <div className="section-head"><div><h2>{editing ? "Ubah kategori & anggaran" : "Tambah kategori"}</h2><p className="muted">Atur batas bulanan. Kosongkan nominal agar statusnya Belum diatur.</p></div>{editing && <button className="icon-btn" data-testid="cancel-edit-category" onClick={() => { setEditing(null); setForm(empty); }}><X size={18} /></button>}</div>
        <form className="user-form" onSubmit={submit}>
          <input data-testid="category-name-input" placeholder="Nama kategori" required minLength={2} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          <input data-testid="category-budget-input" type="number" min="0" step="1" placeholder="Anggaran per bulan (Rp) · opsional" value={form.monthly_budget} onChange={e => setForm({ ...form, monthly_budget: e.target.value })} />
          <button data-testid="save-category-button" className="primary-btn" type="submit"><Plus size={17} /> {editing ? "Simpan perubahan" : "Tambah kategori"}</button>
        </form>
      </section>
      <section className="section-panel">
        <div className="section-head"><div><h2>Anggaran bulan ini</h2><p className="muted">{categories.length} kategori · {s.configured_categories} sudah diatur</p></div></div>
        <div className="budget-list">{categories.map(c => <BudgetRow key={c.id} c={c} onEdit={edit} onDelete={remove} />)}</div>
      </section>
    </div>
  </>;
}
